import React, { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import M3Card from '../../M3Card';
import ToolResult from './ToolResult';
import {
  TRAIN_BOVW_MUTATION,
  EXPORT_LITHICS_MUTATION,
  EXPORT_PUBLICATION_MUTATION,
  downloadExportFile
} from '../../../graphql/tools';

/** Action tools: BOVW similarity training and the two export downloads. */
export default function ActionTools({ publicationId }) {
  const [clusters, setClusters] = useState(20);
  const [format, setFormat] = useState('csv');
  const [numPoints, setNumPoints] = useState(100);
  const [bovwResult, setBovwResult] = useState(null);
  const [lithicsResult, setLithicsResult] = useState(null);
  const [publicationResult, setPublicationResult] = useState(null);
  const [busy, setBusy] = useState(null);

  const [trainBovw] = useMutation(TRAIN_BOVW_MUTATION);
  const [exportLithics] = useMutation(EXPORT_LITHICS_MUTATION);
  const [exportPublication] = useMutation(EXPORT_PUBLICATION_MUTATION);

  const run = (label, task, setResult) => {
    setBusy(label);
    setResult(null);
    task()
      .then((result) => setResult({ ok: true, ...result }))
      .catch((e) => setResult({ ok: false, message: e.message }))
      .finally(() => setBusy(null));
  };

  return (
    <>
      <M3Card variant="outlined">
        <M3Card.Header
          title="Train BOVW similarity"
          subtitle="Ceramic vocabulary + similarity matrix (k-means clusters)"
        />
        <M3Card.Body>
          <div className="tool-controls">
            <label className="tool-field">
              Clusters
              <input
                type="number"
                min="5"
                max="200"
                value={clusters}
                onChange={(e) => setClusters(e.target.value)}
              />
            </label>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy === 'bovw'}
              onClick={() =>
                run(
                  'bovw',
                  async () => {
                    const res = await trainBovw({
                      variables: { id: publicationId, clusters: Number(clusters) || 20 }
                    });
                    const data = res.data.trainBovw;
                    if (!data.success) throw new Error(data.error || 'training failed');
                    return {
                      count: data.similarityCount,
                      filename: `${data.similarityCount} similarities`
                    };
                  },
                  setBovwResult
                )
              }
            >
              {busy === 'bovw' ? 'Training…' : 'Train'}
            </button>
            <ToolResult result={bovwResult} />
          </div>
        </M3Card.Body>
      </M3Card>

      <M3Card variant="outlined">
        <M3Card.Header
          title="Export lithic contours"
          subtitle="Stone tool figures as CSV or JSON"
        />
        <M3Card.Body>
          <div className="tool-controls">
            <label className="tool-field">
              Format
              <select value={format} onChange={(e) => setFormat(e.target.value)}>
                <option value="csv">CSV</option>
                <option value="json">JSON</option>
              </select>
            </label>
            <label className="tool-field">
              Points per contour (2-500)
              <input
                type="number"
                min="2"
                max="500"
                value={numPoints}
                onChange={(e) => setNumPoints(e.target.value)}
              />
            </label>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy === 'lithics'}
              onClick={() =>
                run(
                  'lithics',
                  async () => {
                    const res = await exportLithics({
                      variables: {
                        id: publicationId,
                        format,
                        points: Math.max(2, Math.min(500, Number(numPoints) || 100))
                      }
                    });
                    downloadExportFile(res.data.exportLithics);
                    return { filename: res.data.exportLithics.filename };
                  },
                  setLithicsResult
                )
              }
            >
              {busy === 'lithics' ? 'Exporting…' : 'Export'}
            </button>
            <ToolResult result={lithicsResult} />
          </div>
        </M3Card.Body>
      </M3Card>

      <M3Card variant="outlined">
        <M3Card.Header
          title="Export publication"
          subtitle="Full archive: graves, outlines and SVGs as ZIP"
        />
        <M3Card.Body>
          <div className="tool-controls">
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy === 'publication'}
              onClick={() =>
                run(
                  'publication',
                  async () => {
                    const res = await exportPublication({ variables: { id: publicationId } });
                    downloadExportFile(res.data.exportPublication);
                    return { filename: res.data.exportPublication.filename };
                  },
                  setPublicationResult
                )
              }
            >
              {busy === 'publication' ? 'Exporting…' : 'Export'}
            </button>
            <ToolResult result={publicationResult} />
          </div>
        </M3Card.Body>
      </M3Card>
    </>
  );
}
